/**
 * /v1 announcements + groups contracts (Phase 7).
 */

export type ApiAnnouncement = {
    id: string;
    parishId: string;
    title: string;
    message: string;
    data: unknown;
    status: string;
    createdById: string | null;
    sentAt: string | null;
    createdAt: string;
};

export type ApiGroup = {
    id: string;
    parishId: string;
    title: string;
    description: string | null;
    color: string | null;
    imageUrl: string | null;
    createdAt: string;
    updatedAt: string;
};

export type ApiGroupMember = {
    id: string;
    groupId: string;
    userId: string;
    status: string;
    createdAt: string;
    user: {
        id: string;
        email: string;
        firstName: string | null;
        lastName: string | null;
    };
};

/** UI announcement row (aliases for existing screens). */
export type Announcement = {
    id: string;
    title: string;
    message: string;
    createDate: string;
    status: string;
    createdById: string | null;
    sentAt: string | null;
    /** Kept empty — legacy nested notification payload removed. */
    data: null;
    raw: ApiAnnouncement;
};

export type AnnouncementGroup = {
    id: string;
    title: string;
    description: string;
    contact: string;
    imageUrl: string;
    imgUrl: string;
    color: string | null;
    totalParticipants: string | number;
    raw: ApiGroup;
};

export type AnnouncementGroupMemberRow = {
    id: string;
    userId: string;
    status: string;
    name: string;
    username: string;
    parishionerId: string;
    /** Alias shape for existing table column paths. */
    userResponse: {
        id: string;
        name: string;
        username: string;
        email: string;
        firstName: string;
        lastName: string;
    };
};

export type CreateAnnouncementPayload = {
    title: string;
    message: string;
    groupIds?: string[];
    userIds?: string[];
};

export type CreateGroupPayload = {
    title: string;
    description?: string;
    color?: string;
};

export type UpdateGroupPayload = {
    title?: string;
    description?: string;
    color?: string;
};

export function normalizeAnnouncement(row: ApiAnnouncement): Announcement {
    return {
        id: row.id,
        title: row.title || '',
        message: row.message || '',
        createDate: row.sentAt || row.createdAt || '',
        status: row.status || '',
        createdById: row.createdById ?? null,
        sentAt: row.sentAt,
        data: null,
        raw: row,
    };
}

export function normalizeGroup(row: ApiGroup, totalParticipants?: number): AnnouncementGroup {
    const description = row.description || '';
    return {
        id: row.id,
        title: row.title || '',
        description,
        contact: description,
        imageUrl: row.imageUrl || '',
        imgUrl: row.imageUrl || '',
        color: row.color,
        totalParticipants: totalParticipants ?? '',
        raw: row,
    };
}

export function normalizeGroupMember(row: ApiGroupMember): AnnouncementGroupMemberRow {
    const firstName = row.user?.firstName || '';
    const lastName = row.user?.lastName || '';
    const name = [firstName, lastName].filter(Boolean).join(' ').trim() || row.user?.email || '';
    const username = row.user?.email || '';
    return {
        id: row.userId,
        userId: row.userId,
        status: row.status || 'ACTIVE',
        name,
        username,
        parishionerId: '',
        userResponse: {
            id: row.userId,
            name,
            username,
            email: username,
            firstName,
            lastName,
        },
    };
}
